/* eslint-disable consistent-return */
import {WindowMode} from '@venly/connect';
import {AbstractConnector} from '@web3-react/abstract-connector';
import {ConnectorUpdate} from '@web3-react/types';
import {VENLY_CHAINS, VENLY_CHAINS_LIVE, VENLY_SECRET_TYPE} from './const';
import {
  ConfirmationType,
  ConstructorOptions,
  Venly,
  VenlyChains,
  Wallet,
} from './types';
import {getAuthenticatedWalletState, setWalletIdCookies} from './utils';

export class VenlyConnector extends AbstractConnector {
  private readonly clientId: string;

  private readonly options: ConstructorOptions;

  private readonly chain: VenlyChains;

  private walletId: string | undefined;

  private isSwitched = false;

  constructor(clientId: string, options: ConstructorOptions) {
    super();
    this.clientId = clientId;
    this.options = {
      chains: options.chains,
      environment: options.environment,
      windowMode: options.windowMode,
    };
    const chainFromOption = options.chains[0];
    this.chain = chainFromOption;
    this.isSwitched = options.isSwitched || false;
  }

  public venly: Venly | undefined;

  public async getProvider(): Promise<Venly | undefined> {
    return this.venly;
  }

  public async getChainId(): Promise<string | number> {
    const chainId =
      this.options.environment === 'staging'
        ? VENLY_CHAINS[this.chain]
        : VENLY_CHAINS_LIVE[this.chain];
    return chainId;
  }

  private async getPrimaryAccount(): Promise<Wallet | null | undefined> {
    try {
      const allWallets = await this.venly?.api.getWallets();
      const primaryWalletFromChain = allWallets?.find(
        ({secretType, primary}) =>
          secretType === this.chain && (primary === true || primary === false),
      );
      if (primaryWalletFromChain) return primaryWalletFromChain;
      if (
        !primaryWalletFromChain &&
        allWallets?.length &&
        allWallets?.filter(({secretType}) =>
          VENLY_SECRET_TYPE.includes(secretType),
        ).length !== 0
      ) {
        const primaryWallet = allWallets?.find(
          ({primary}) => primary === true || primary === false,
        ) || {id: ''};
        const {id: walletId} = primaryWallet;
        const signer = this.venly?.createSigner(this.options.windowMode);
        if (!signer) throw new Error('Create signer not defined.');
        try {
          return new Promise(() => {
            setTimeout(async () => {
              await signer.importWallet(
                {
                  walletId,
                  to: this.chain,
                  confirmationRequestType: 'IMPORT_WALLET' as ConfirmationType,
                },
                {
                  correlationID: `${Date.now()}`,
                },
              );
            }, 100);
          });
        } catch (e) {
          throw new Error('User denied to import wallet.');
        }
      } else {
        throw new Error("Regsistration won't work with venly.");
        // return new Promise(() => {
        //   setTimeout(async () => {
        //     await this.venly?.flows.manageWallets(this.chain, {
        //       windowMode: WindowMode.REDIRECT,
        //       correlationID: `${Date.now()}`,
        //     });
        //     // await this.venly?.flows.linkWallets({
        //     //   windowMode: WindowMode.REDIRECT,
        //     //   correlationID: `${Date.now()}`,
        //     // });
        //   }, 100);
        // });
      }
    } catch (e: any) {
      if (e && e?.message) {
        throw new Error(
          e?.message === 'Something went wrong while trying to open the popup'
            ? 'Please disable popup blocker.'
            : 'The user rejected the request.',
        );
      }
    }
  }

  public async getAccount(account?: Wallet | null): Promise<string | null> {
    if (!account) return null;
    const {address, id} = account;
    this.walletId = id;
    return address;
  }

  public async getWalletId(): Promise<string> {
    return this.walletId || '';
  }

  public async deactivate(): Promise<void> {
    await this.venly?.logout();
  }

  public async activate(): Promise<ConnectorUpdate<string | number>> {
    if (!this.venly) {
      const {VenlyConnect} = await import('@venly/connect');
      this.venly = new VenlyConnect(this.clientId, this.options);
    }
    const {chainId, address, provider} = getAuthenticatedWalletState() || {
      chainId: undefined,
      address: undefined,
      provider: undefined,
      userId: undefined,
    };

    if (chainId && address && provider === 'venly' && !this.isSwitched) {
      return {
        chainId,
        account: address,
        provider: await this.getProvider(),
      };
    }

    let primaryAccount = null;
    const authenticatedState = await this.venly.checkAuthenticated();
    const {isAuthenticated} = authenticatedState;
    if (isAuthenticated) {
      primaryAccount = await this.getPrimaryAccount();
    } else {
      await this.venly?.flows.authenticate({
        windowMode: this.options.windowMode,
      });
    }
    if (primaryAccount) {
      const {id} = primaryAccount;
      setWalletIdCookies(id);
    }

    return {
      chainId: await this.getChainId(),
      account: await this.getAccount(primaryAccount),
      provider: await this.getProvider(),
    };
  }
}
